// The local map's terrain (mockup family of the overworld): the adventure's site graph laid on a
// seeded pixel board in the location's kit — the bog road in the Mirewood mud, the Stone Bridge on
// its river, a wood among trees. Sites come from the forge's (x, y) percent hints; edges become
// orthogonal elbows between them (the cells under them and around each site stay clear), and a
// river under an edge gets planks. Deterministic from the instance, so the party sees one map.
import { PLANKS, PROPS } from '../../../game/tiling/oryx';
import { OW_H, OW_W, type MapProp } from './overworld';

export const LM_W = OW_W, LM_H = OW_H;
/** Where sites may sit (cell box), clear of the screen's panels at 1280×800 and up. */
const BOX = { x0: 13.5, x1: 30.5, y0: 6.5, y1: 15.5 };

export interface LocalSite { id: number; x: number; y: number }
export interface LocalEdge { a: number; b: number }
export interface LocalBoard { rows: string[]; fills: MapProp[]; props: MapProp[]; nodes: Map<number, { x: number; y: number }>; paths: Array<Array<[number, number]>>; kit: string }

/** The view kit for a location name. */
export function localKit(location: string): string {
  const n = location.toLowerCase();
  if (/mire|bog|marsh|fen|swamp|causeway/.test(n)) return 'mirewood';
  if (/autumn|orchard|field/.test(n)) return 'autumn';
  return 'forest';
}

function hash(seed: number, x: number, y: number, salt: number): number {
  let h = (seed ^ Math.imul(x + 31, 0x9e3779b1) ^ Math.imul(y + 17, 0x85ebca6b) ^ Math.imul(salt, 0xc2b2ae35)) >>> 0;
  h ^= h >>> 15; h = Math.imul(h, 0x2c1b3c6d); h ^= h >>> 12; h = Math.imul(h, 0x297a2d39); h ^= h >>> 15;
  return (h >>> 0) / 4294967296;
}
/** Smooth value noise on a lattice of `step` cells. */
function noise(seed: number, salt: number, step: number) {
  const s = (t: number) => t * t * (3 - 2 * t);
  return (x: number, y: number) => {
    const fx = x / step, fy = y / step, i = Math.floor(fx), j = Math.floor(fy), u = s(fx - i), v = s(fy - j);
    const g = (a: number, b: number) => hash(seed, a, b, salt);
    return (g(i, j) * (1 - u) + g(i + 1, j) * u) * (1 - v) + (g(i, j + 1) * (1 - u) + g(i + 1, j + 1) * u) * v;
  };
}

export function generateLocal(seed: number, location: string, sites: readonly LocalSite[], edges: readonly LocalEdge[]): LocalBoard {
  const kit = localKit(location);
  const bridge = /bridge|ford/i.test(location);
  const W = LM_W, H = LM_H;
  const grid: string[][] = Array.from({ length: H }, () => Array.from({ length: W }, () => '.'));
  const k = (x: number, y: number) => `${x},${y}`;
  // The forge keeps its rows near 22–34 and 66–78: stretch them about the middle.
  const nodes = new Map<number, { x: number; y: number }>();
  for (const s of sites) {
    const ty = Math.min(100, Math.max(0, 50 + (s.y - 50) * 1.4)) / 100;
    nodes.set(s.id, { x: Math.round(BOX.x0 + (s.x / 100) * (BOX.x1 - BOX.x0)) + 0.5, y: Math.round(BOX.y0 + ty * (BOX.y1 - BOX.y0)) + 0.5 });
  }
  const paths = edges.map((e) => {
    const a = nodes.get(e.a), b = nodes.get(e.b);
    if (!a || !b) return [] as Array<[number, number]>;
    const elbow: [number, number] = Math.abs(a.x - b.x) >= Math.abs(a.y - b.y) ? [b.x, a.y] : [a.x, b.y];
    return [[a.x, a.y], elbow, [b.x, b.y]] as Array<[number, number]>;
  });
  const keep = new Set<string>();
  for (const n of nodes.values()) for (let dy = -2; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) keep.add(k(Math.floor(n.x) + dx, Math.floor(n.y) + dy));
  const road = new Set<string>();
  for (const p of paths) for (let i = 0; i < p.length - 1; i++) {
    const [ax, ay] = p[i]!, [bx, by] = p[i + 1]!;
    const n = Math.max(Math.abs(bx - ax), Math.abs(by - ay)) * 2;
    for (let s = 0; s <= n; s++) { const x = Math.floor(ax + ((bx - ax) * s) / Math.max(1, n)), y = Math.floor(ay + ((by - ay) * s) / Math.max(1, n)); keep.add(k(x, y)); road.add(k(x, y)); }
  }

  // A river through the middle for a bridge; mud and pools for the mire; trees by noise everywhere.
  const props: MapProp[] = [];
  if (bridge) {
    const ph = hash(seed, 1, 2, 3) * Math.PI * 2;
    for (let y = 0; y < H; y++) { const rx = 21 + Math.round(1.2 * Math.sin(y * 0.4 + ph)); for (const x of [rx, rx + 1]) { grid[y]![x] = '~'; if (road.has(k(x, y))) props.push({ frame: PLANKS.ew[x % 3]!, x, y }); } }
  }
  const tn = noise(seed, 1, 4), mn = noise(seed, 2, 3), pn = noise(seed, 3, 3);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (grid[y]![x] === '~') continue;
    const clear = keep.has(k(x, y));
    const edge = Math.min(x, W - 1 - x, y, H - 1 - y) < 2 ? 0.12 : 0;
    if (kit === 'mirewood') {
      if (!clear && mn(x, y) > 0.6) grid[y]![x] = mn(x, y) > 0.74 ? '~' : ',';
      else if (!clear && tn(x, y) + edge > 0.6) grid[y]![x] = 'T';
    } else {
      if (!clear && tn(x, y) + edge > 0.6) grid[y]![x] = 'T';
      else if (!clear && pn(x, y) > 0.7 && hash(seed, x, y, 9) > 0.3) props.push({ frame: kit === 'autumn' ? PROPS.trees.autumnSingle[(x + y) % 3]! : PROPS.trees.pineSingle[(x + y) % 3]!, x, y });
    }
  }
  props.sort((a, b) => a.y - b.y || a.x - b.x);
  return { rows: grid.map((r) => r.join('')), fills: [], props, nodes, paths, kit };
}

/** The road the party walks from one site to a neighbour: the edge's elbow path, turned to start at `from`. */
export function localRoute(board: Pick<LocalBoard, 'paths'>, edges: readonly LocalEdge[], from: number, to: number): Array<[number, number]> | undefined {
  const i = edges.findIndex((e) => (e.a === from && e.b === to) || (e.a === to && e.b === from));
  const path = board.paths[i];
  if (!path || path.length < 2) return undefined;
  return edges[i]!.a === from ? path.slice() : [...path].reverse();
}
